import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { formatPKR } from "../lib/format";
import { Minus, Plus, MessageCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        setActiveImg(0);
        setQty(1);
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="aspect-[3/4] bg-sand animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 bg-sand w-24 animate-pulse" />
            <div className="h-12 bg-sand w-3/4 animate-pulse" />
            <div className="h-32 bg-sand animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-32 text-center">
        <h1 className="font-serif text-4xl text-ink">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block link-underline text-xs uppercase tracking-widest text-gold">
          Back to shop
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to cart`, {
      description: `${qty} × ${formatPKR(product.price)}`,
    });
  };

  const images = product.images && product.images.length > 0 ? product.images : [""];

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16">
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ash hover:text-ink transition mb-8">
        <ArrowLeft size={14} />
        Back to shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
        {/* Gallery */}
        <div className="flex flex-col-reverse md:flex-row gap-4">
          <div className="flex md:flex-col gap-3 overflow-x-auto no-scrollbar">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`thumb-${i}`}
                className={`flex-shrink-0 w-20 h-24 overflow-hidden bg-sand border ${
                  activeImg === i ? "border-gold" : "border-transparent"
                } transition`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 aspect-[3/4] bg-sand overflow-hidden">
            <img
              src={images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Details */}
        <div className="lg:pt-8">
          <div className="eyebrow mb-3">{product.brand}</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink tracking-tight leading-[0.95]">
            {product.name}
          </h1>
          <div className="mt-5 flex items-baseline gap-4">
            <div className="font-sans text-2xl font-medium text-ink tracking-wide">{formatPKR(product.price)}</div>
            <div className="text-xs text-ash uppercase tracking-widest">
              {product.volume_ml}ml · {product.category}
            </div>
          </div>

          <p className="mt-8 text-base text-graphite leading-relaxed font-light max-w-lg">
            {product.description}
          </p>

          {/* Notes */}
          <div className="mt-10 border-y border-gold-soft py-6 space-y-4">
            {[
              { label: "Top notes", value: product.notes_top },
              { label: "Heart notes", value: product.notes_middle },
              { label: "Base notes", value: product.notes_base },
            ].map((row) => (
              row.value && row.value.length > 0 ? (
                <div key={row.label} className="grid grid-cols-[140px_1fr] gap-4 items-baseline">
                  <div className="eyebrow text-ash">{row.label}</div>
                  <div className="text-sm text-ink">{row.value.join(" · ")}</div>
                </div>
              ) : null
            ))}
          </div>

          {/* Quantity + Add to cart */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <div className="flex items-center border border-ink h-14">
              <button
                data-testid="qty-dec-btn"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-4 h-full hover:bg-sand transition"
                aria-label="Decrease"
              >
                <Minus size={14} />
              </button>
              <span className="px-5 text-sm tabular-nums min-w-[3ch] text-center">{qty}</span>
              <button
                data-testid="qty-inc-btn"
                onClick={() => setQty((q) => q + 1)}
                className="px-4 h-full hover:bg-sand transition"
                aria-label="Increase"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              className="flex-1 bg-ink text-cream h-14 px-8 text-xs uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors"
            >
              Add to cart
            </button>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-ash">
            <MessageCircle size={14} className="text-whatsapp" />
            Checkout sends your order via WhatsApp to confirm availability.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
